import { useState, useRef } from "react";
import { Camera, Upload, X, Loader2, AlertCircle, CheckCircle2, ImageIcon, Info } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const JENIS_API_MAP = {
    pengambilan: "bon_kain",
    bahan_penolong: "bon_peralatan",
    setoran: "setor",
    kasbon: "kasbon",
    rijek: "reject",
    setor_perbaikan: "setor_perbaikan",
};

const JENIS_LABEL_MAP = {
    pengambilan: "Pengambilan Kain",
    bahan_penolong: "Bahan Penolong",
    setoran: "Setoran",
    kasbon: "Kasbon",
    rijek: "Rijek",
    setor_perbaikan: "Setor Perbaikan",
};

export function ScanNotaModal({ open, jenis, onClose, onExtracted }) {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [result, setResult] = useState(null);
    const inputRef = useRef(null);
    const cameraRef = useRef(null);

    if (!open) return null;

    const pickFile = (f) => {
        setErr("");
        setResult(null);
        if (!f) return;
        if (f.size > 5 * 1024 * 1024) { setErr("Ukuran gambar maksimal 5MB"); return; }
        if (!/^image\/(jpeg|jpg|png|webp|heic|heif)$/i.test(f.type)) {
            setErr(`Format tidak didukung (${f.type}). Pakai JPG/PNG/WEBP/HEIC.`);
            return;
        }
        setFile(f);
        const url = URL.createObjectURL(f);
        setPreview(url);
    };

    const handleScan = async () => {
        if (!file) return;
        setLoading(true); setErr(""); setResult(null);
        try {
            const fd = new FormData();
            fd.append("image", file);
            fd.append("jenis_transaksi", JENIS_API_MAP[jenis] || jenis);
            const r = await api.post("/ai/scan-nota", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60_000,
            });
            setResult(r.data);
            if (!r.data.ok) {
                setErr("AI tidak dapat membaca nota dengan jelas. Coba foto ulang dengan pencahayaan lebih baik.");
            }
        } catch (e) {
            setErr(e.response?.data?.detail || "Scan gagal. Coba lagi.");
        } finally { setLoading(false); }
    };

    const useResult = () => {
        if (!result?.extracted) return;
        onExtracted(result.extracted);
        reset();
    };

    const reset = () => {
        setFile(null);
        if (preview) URL.revokeObjectURL(preview);
        setPreview("");
        setResult(null);
        setErr("");
    };

    const close = () => { reset(); onClose(); };

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex md:items-center md:justify-center md:p-4" onClick={close} data-testid="scan-nota-modal">
            <div className="bg-white w-full h-full md:h-auto md:max-h-[90vh] md:w-full md:max-w-xl flex flex-col md:rounded-2xl md:shadow-xl md:border md:border-[var(--border)] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--border)] flex-shrink-0">
                    <button onClick={close} className="order-1 md:order-2 text-slate-500 hover:text-slate-900 rounded-full p-1.5 hover:bg-slate-100" data-testid="scan-close" aria-label="Tutup">
                        <X className="w-5 h-5" />
                    </button>
                    <h3 className="order-2 md:order-1 flex-1 font-bold text-slate-900 flex items-center gap-2 text-base md:text-sm truncate">
                        <Camera className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                        <span className="truncate">Scan Nota — {JENIS_LABEL_MAP[jenis] || jenis}</span>
                    </h3>
                </div>
                <div className="p-5 flex-1 overflow-y-auto">

                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-800 mb-4 flex items-start gap-2" data-testid="scan-warning">
                    <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <div>
                        AI hanya membantu membaca nota — <b>wajib periksa ulang</b> setiap field sebelum simpan. AI bisa salah atau melewatkan info.
                    </div>
                </div>

                {!preview && (
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <button
                            onClick={() => inputRef.current?.click()}
                            data-testid="scan-pilih-foto"
                            className="border-2 border-dashed border-slate-300 rounded-lg p-6 hover:border-indigo-400 hover:bg-indigo-50 flex flex-col items-center gap-2 text-sm text-slate-600"
                        >
                            <Upload className="w-6 h-6 text-slate-400" />
                            <span className="font-medium">Pilih Foto</span>
                            <span className="text-[11px] text-slate-400">Dari galeri HP / komputer</span>
                        </button>
                        <button
                            onClick={() => cameraRef.current?.click()}
                            data-testid="scan-jepret-kamera"
                            className="border-2 border-dashed border-slate-300 rounded-lg p-6 hover:border-teal-400 hover:bg-teal-50 flex flex-col items-center gap-2 text-sm text-slate-600"
                        >
                            <Camera className="w-6 h-6 text-slate-400" />
                            <span className="font-medium">Jepret Kamera</span>
                            <span className="text-[11px] text-slate-400">Foto langsung dari HP</span>
                        </button>
                        <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => pickFile(e.target.files?.[0])} />
                        <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => pickFile(e.target.files?.[0])} />
                    </div>
                )}

                {preview && !result && (
                    <div className="mb-4">
                        <div className="relative mb-2 bg-slate-100 rounded overflow-hidden" style={{ maxHeight: 320 }}>
                            <img src={preview} alt="preview" className="w-full h-full object-contain max-h-80" />
                            <button
                                onClick={reset}
                                data-testid="scan-reset"
                                className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded p-1 shadow"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <div className="text-[11px] text-slate-500 mb-2">{file?.name} · {Math.round((file?.size || 0) / 1024)} KB</div>
                        <button
                            onClick={handleScan}
                            disabled={loading}
                            data-testid="scan-baca-nota"
                            className="w-full px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white rounded font-medium text-sm inline-flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" /> AI sedang membaca nota... (3-10 detik)
                                </>
                            ) : (
                                <>
                                    <ImageIcon className="w-4 h-4" /> Baca Nota dengan AI
                                </>
                            )}
                        </button>
                    </div>
                )}

                {err && (
                    <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700 flex items-start gap-2 mb-3" data-testid="scan-error">
                        <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <div>{err}</div>
                    </div>
                )}

                {result?.ok && result.extracted && (
                    <div className="mb-3" data-testid="scan-result">
                        <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                            <div className="flex items-center gap-2 mb-2">
                                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                <span className="font-semibold text-sm text-emerald-900">Hasil Pembacaan AI</span>
                                <span className={`badge ${result.confidence === "high" ? "badge-hijau" : result.confidence === "medium" ? "badge-kuning" : "badge-merah"} text-[10px] ml-auto`}>
                                    {result.confidence === "high" ? "Yakin" : result.confidence === "medium" ? "Lumayan" : "Kurang"}
                                </span>
                            </div>
                            <div className="text-xs space-y-1 bg-white rounded p-2 max-h-48 overflow-y-auto">
                                {Object.entries(result.extracted).map(([k, v]) => (
                                    <div key={k} className="flex gap-2">
                                        <span className="font-mono text-slate-500 min-w-[120px]">{k}:</span>
                                        <span className={`${v === null || v === "" ? "text-slate-400 italic" : "font-medium text-slate-900"}`}>
                                            {v === null || v === "" ? "(kosong)" : Array.isArray(v) ? v.join(", ") || "(kosong)" : String(v)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            {result.warnings?.length > 0 && (
                                <div className="mt-2 text-[11px] text-amber-700">
                                    ⚠ {result.warnings.join(" · ")}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2 mt-3">
                            <button
                                onClick={reset}
                                data-testid="scan-ulang"
                                className="px-3 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 rounded text-xs font-medium"
                            >
                                Scan Ulang
                            </button>
                            <button
                                onClick={useResult}
                                data-testid="scan-pakai-hasil"
                                className="flex-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-medium"
                            >
                                Pakai Hasil & Isi Form →
                            </button>
                        </div>
                    </div>
                )}
                </div>
            </div>
        </div>
    );
}
